// useMarket.js
// Custom hook for managing Market state and data operations

import { useState, useEffect, useMemo } from 'react';
import fetchListings from '../../utils/fetchListings';

const useMarket = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allListings, setAllListings] = useState([]); // For client-side search
  const [filters, setFilters] = useState({
    category: "",
    minPrice: "",
    maxPrice: "",
  });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");

  // Map sort option to Firestore orderBy/orderDir
  const sortParams = useMemo(() => {
    switch (sort) {
      case "priceLow":
        return { orderBy: "price", orderDir: "asc" };
      case "priceHigh":
        return { orderBy: "price", orderDir: "desc" };
      case "newest":
      default:
        return { orderBy: "createdAt", orderDir: "desc" };
    }
  }, [sort]);

  // Filter listings by search (title only, case-insensitive)
  const filteredListings = useMemo(() => {
    return search
      ? allListings.filter((listing) =>
          listing.title && listing.title.toLowerCase().includes(search.toLowerCase())
        )
      : listings;
  }, [search, allListings, listings]);

  // Fetch listings whenever filters or sort change
  useEffect(() => {
    const loadListings = async () => {
      setLoading(true);
      try {
        const filterParams = {
          category: filters.category || null,
          minPrice: filters.minPrice && filters.minPrice !== "" ? Number(filters.minPrice) : null,
          maxPrice: filters.maxPrice && filters.maxPrice !== "" ? Number(filters.maxPrice) : null,
          ...sortParams,
        };
        
        const data = await fetchListings(filterParams);
        
        setListings(data);
        setAllListings(data); // Save unfiltered for search
      } catch (error) {
        setListings([]);
        setAllListings([]);
      } finally {
        setLoading(false);
      }
    };

    loadListings();
  }, [filters, sortParams]);

  return {
    listings: filteredListings,
    loading,
    filters,
    setFilters,
    search,
    setSearch,
    sort,
    setSort
  };
};

export default useMarket;